import classNames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Image, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '../../../../shared/components/Button';
import ProgressBar from '../../../../shared/components/ProgressBar/ProgressBar';
import useBreakpoint from '../../../../shared/hooks/useBreakpoint';
import { OFFER_STATUSES } from '../../../../utils/constants';
import { setEventProps } from '../../../../utils/heap';
import CampaignHeader from '../components/CampaignHeader';
import ContentApproval from '../components/ContentApproval';
import ObjectAttributesBox from '../components/ObjectAttributesBox/ObjectAttributesBox';
import Applying from '../userOffer/Applying';
import CampaignCanceled from '../userOffer/CampaignCanceled';
import Completed from '../userOffer/Completed';
import NotSelected from '../userOffer/NotSelected';
import PendingEvent from '../userOffer/PendingEvent';
import PendingStats from '../userOffer/PendingStats';
import PostingPhase from '../userOffer/PostingPhase';
import WaitingProduct from '../userOffer/WaitingProduct';
import './General.scss';
import Glance from './Glance';
import PendingPayment from './PendingPayment';
import SettingsMenu from './SettingsMenu';
import Survey from './Survey';
import Timeline from './Timeline';
import InfluencerGuidanceBox from '../components/InfluencerGuidanceBox';

/**
 * @typedef GeneralParams
 * @property {import('../../../../reducers/offer.reducer').Offer} offer
 *
 * @param {GeneralParams} useParams
 */
const General = ({ offer }) => {
  if (!offer) return null;

  const breakpoint = useBreakpoint();
  const { t } = useTranslation();
  let userSignedUp = useSelector(store => get(store, 'offer.userSignedUp'));

  { /* this is the userOffer object, we will use it a lot in all the child components,
      renaming so we know what it is */ }
  const userOffer = offer.user_offer;
  const status = get(userOffer, 'status');
  const applyingSurvey = get(offer, 'is_survey') && ![OFFER_STATUSES.DONE, OFFER_STATUSES.WAITING_REWARD].includes(status);

  setEventProps({ 'campaign_status': get(userOffer, 'status') });

  const [state, setState] = useState({
    openSettingsMenu: false,
    renderSettingsMenu: false,
    offerDeclined: false
  });

  const toggleSettingsMenu = useCallback((open, render = true) => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
    setState({
      ...state,
      renderSettingsMenu: render,
      openSettingsMenu: open,
    });
  }, [state]);

  useEffect(() => {
    setState({
      ...state,
      offerDeclined: get(userOffer, 'status')
        ? [OFFER_STATUSES.DECLINED, OFFER_STATUSES.CANCELED].includes(userOffer.status)
        : false
    });
  }, [userOffer]);

  let ProgressRender = null;
  if (userSignedUp || state.offerDeclined) {
    let progress = 0;

    switch (status) {
      case OFFER_STATUSES.APPLYING:
      case OFFER_STATUSES.IN_WAITLIST:
        progress = 0;
        break;
      case OFFER_STATUSES.NOT_SELECTED:
        break;
      case OFFER_STATUSES.WAITING_PRODUCT:
        progress = 25;
        break;
      case OFFER_STATUSES.PENDING_CONTENT_SUBMISSION:
      case OFFER_STATUSES.CONTENT_WORK_NEEDED:
      case OFFER_STATUSES.WAITING_CONTENT_APPROVAL:
        progress = 50;
        break;
      case OFFER_STATUSES.PENDING_POST:
      case OFFER_STATUSES.PENDING_COMPLETION:
      case OFFER_STATUSES.WORK_NEEDED:
        progress = 75;
        break;
      case OFFER_STATUSES.PENDING_EVENT:
        progress = 25;
        break;
      case OFFER_STATUSES.ATTEND_EVENT:
        break;
      case OFFER_STATUSES.PENDING_REVIEW:
        progress = 75;
        break;
      case OFFER_STATUSES.PENDING_STATS:
        progress = 75;
        break;
      case OFFER_STATUSES.WAITING_REWARD:
        progress = 100;
        break;
      case OFFER_STATUSES.DONE:
        progress = 100;
        break;
      case OFFER_STATUSES.REMOVED:
      case OFFER_STATUSES.DECLINED:
        progress = 0;
        break;
      case OFFER_STATUSES.CANCELED:
        progress = 0;
        break;
      case OFFER_STATUSES.GHOSTED:
        break;
    }

    ProgressRender = () => (
      <React.Fragment>
        <div className="text-medium-2 mb-2">
          {t('influencers.pages.campaign.progress')}
        </div>
        <ProgressBar now={applyingSurvey ? 50 : progress} />
      </React.Fragment>
    );
  }

  const TimelineRender = useCallback(() => (
    <Col xs={12} md={4} sm={3}>
      <Timeline offer={offer} />
      {/* Hides campaign settings for users in next cases
       * Not signed up into campaign
       * Not selected user
       * Declined user
       * Canceled by user
       */}
      {(userSignedUp &&
        ![OFFER_STATUSES.NOT_SELECTED, OFFER_STATUSES.DECLINED, OFFER_STATUSES.CANCELED]
          .includes(userOffer.status)) ?
        <div className="center">
          <Button variant="tertiary" onClick={() => { toggleSettingsMenu(true, true); }} >
            {t('influencers.pages.campaign.settings')}
            <i className="fal fa-arrow-right pl-2" />
          </Button>
        </div>
        : null}
    </Col>
  ), [offer.timelines, userSignedUp, state.offerDeclined, toggleSettingsMenu]);


  const renderStatus = useCallback((status) => {
    /* for instance, if the userOffer.status == applying, it will show the <Applying /> component,
      the Applying component will have the content in this ticket:
      https://obviouslygreat.atlassian.net/browse/ENG-5389
      This figma screen is 3.9.0, the full list of each status and which figma screen
      corresponds to them are listed here:
      https://obviouslygreat.atlassian.net/wiki/spaces/PT/pages/1396146177/Influencer+Experience-+Active+Campaign+Statuses
    */
    if (applyingSurvey) { return <Survey offer={offer} />; }
    switch (status) {
      case OFFER_STATUSES.APPLYING:
      case OFFER_STATUSES.IN_WAITLIST:
        return <Applying offer={offer} />;
      case OFFER_STATUSES.NOT_SELECTED:
        return <NotSelected offer={offer} />;
      case OFFER_STATUSES.WAITING_PRODUCT:
        return <WaitingProduct offer={offer} />;
      case OFFER_STATUSES.PENDING_CONTENT_SUBMISSION:
      case OFFER_STATUSES.CONTENT_WORK_NEEDED:
      case OFFER_STATUSES.WAITING_CONTENT_APPROVAL:
        return <ContentApproval offer={offer} />;
      case OFFER_STATUSES.PENDING_POST:
      case OFFER_STATUSES.PENDING_COMPLETION:
      case OFFER_STATUSES.WORK_NEEDED:
      case OFFER_STATUSES.PENDING_REVIEW:
        return <PostingPhase offer={offer} />;
      case OFFER_STATUSES.PENDING_EVENT:
        return <PendingEvent offer={offer} />;
      case OFFER_STATUSES.PENDING_STATS:
        return <PendingStats offer={offer} />;
      case OFFER_STATUSES.WAITING_REWARD:
        return <PendingPayment offer={offer} />;
      case OFFER_STATUSES.DONE:
        return <Completed offer={offer} />;
      case OFFER_STATUSES.REMOVED:
      case OFFER_STATUSES.CANCELED:
        return <CampaignCanceled offer={offer} userOffer={userOffer} />;
      case OFFER_STATUSES.GHOSTED:
        return status;
    }
  }, [offer, userOffer]);

  // remove admin check when tipalti is ready
  const tipaltiPaymentSetupNeeded = offer.tipalti_payment_processing && !user.is_payable_by_tipalti && user.admin;
  const hasObjectAttributeQuestions = offer.object_attribute_metadata.filter(oa => oa.influencer_ui_view === 'general').length > 0;

  return (
    <React.Fragment>
      {(!userSignedUp && !state.offerDeclined) && <CampaignHeader campaignPic={offer.headline_pic || offer.pic} isJoined={userSignedUp} />}
      <div className="offer-wrapper page" style={{ pointerEvents: tipaltiPaymentSetupNeeded ? 'none' : 'auto' }}>
        <Container>
          <Row className="mb-5">
            {(userSignedUp || state.offerDeclined) &&
              <Col xs={5} sm={2}>
                <CampaignHeader
                  campaignPic={offer.pic}
                  isJoined={userSignedUp} />
              </Col>
            }
            <Col className={classNames("flex-column", { 'mb-5': (!userSignedUp && !state.offerDeclined) })}
              xs={(userSignedUp || state.offerDeclined) ? 7 : 12}
              md={(userSignedUp || state.offerDeclined) ? 10 : 8}
              sm={(userSignedUp || state.offerDeclined) ? 10 : 9}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <Row>
                  <Col>
                    <Image className='image-fluid brand-pic' src={offer.brand_pic} />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {(!userSignedUp && !state.offerDeclined)
                      ? <React.Fragment>
                        {breakpoint !== 'xs' ?
                          <div>
                            <h2 className='mb-5'>{offer.title}</h2>
                            <h4 className='mb-2'>
                              {
                                offer.is_survey ?
                                  t('influencers.pages.campaign.survey_goal') :
                                  t('influencers.pages.campaign.goal')
                              }
                            </h4>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: offer.description
                              }}>
                            </div>
                          </div>
                          :
                          <div>
                            <h3 className='mb-4'>{offer.title}</h3>
                            <h5 className='mb-2'>
                              {
                                offer.is_survey ?
                                  t('influencers.pages.campaign.survey_goal') :
                                  t('influencers.pages.campaign.goal')
                              }
                            </h5>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: offer.description
                              }}>
                            </div>
                          </div>
                        }
                      </React.Fragment>
                      : <React.Fragment>
                        {breakpoint !== 'xs' ? <h4>{offer.title}</h4> : <h6>{offer.title}</h6>}
                      </React.Fragment>
                    }
                  </Col>
                </Row>
              </div>
              {(userSignedUp || state.offerDeclined) && breakpoint !== 'xs' ? <ProgressRender /> : ''}
            </Col>
            {(userSignedUp || state.offerDeclined) && breakpoint === 'xs' ?
              <Col xs={12}>
                <ProgressRender />
              </Col> : ''
            }
            {(!userSignedUp && !state.offerDeclined) && <TimelineRender />}
          </Row>
          {
            userSignedUp ?
              <Row>
                <Col xs={12} md={!applyingSurvey && 8} sm={!applyingSurvey && 9}>
                  {[
                    OFFER_STATUSES.PENDING_POST,
                    OFFER_STATUSES.PENDING_COMPLETION,
                    OFFER_STATUSES.WORK_NEEDED,
                    OFFER_STATUSES.PENDING_REVIEW
                  ].includes(get(userOffer, 'status')) &&
                    <InfluencerGuidanceBox
                      userOffer={userOffer}
                      objectAttributeMetadata={get(offer, 'object_attribute_metadata', [])}
                      status={get(userOffer, 'status')}
                    />
                  }
                  {hasObjectAttributeQuestions ? <ObjectAttributesBox offer={offer} /> : null}
                  {renderStatus(get(userOffer, 'status'))}
                </Col >
                {!applyingSurvey && <TimelineRender />}
              </Row>
              : (state.offerDeclined ?
                <Row>
                  <Col xs={12} md={8} sm={9}>
                    <CampaignCanceled offer={offer} userOffer={userOffer} />
                  </Col >
                  <TimelineRender />
                </Row> : ''
              )
          }
        </Container>
        {(!userSignedUp && !state.offerDeclined) ? <Glance offer={offer} /> : ''}
      </div >
      {state.renderSettingsMenu && <SettingsMenu
        open={state.openSettingsMenu}
        onRequestClose={toggleSettingsMenu}
        onAfterClose={() => toggleSettingsMenu(false, false)}
      />}
    </React.Fragment>

  );
};

General.propTypes = {
  offer: PropTypes.object.isRequired
};

export default General;
